import showNotification from 'components/extras/showNotification';
import { usePlanningData } from 'pages/planning/hooks/usePlanningData';
import AuthContext from 'contexts/authContext';
import { Form } from 'formik';
import { useContext, useRef, useState } from 'react';
import { deleteAPI, postAPI } from 'apis/backend/apiNode';
import { Badge, Button, Col, Image, Modal, Row, Spinner, } from 'react-bootstrap';
import Card, { CardBody } from 'components/bootstrap/Card';
import Textarea from 'components/bootstrap/forms/Textarea';
import Icon from 'components/icon/Icon';
import moment from 'moment';
import ImageVisualisation from 'components/ImageVisualisation';


const PAIEMENT_STATUTS = {
  EN_ATTENTE: "en_attente",
  VALIDE: "validé",  // Changed from "valide" to match backend enum
  REFUSE: "refusé",  // Changed from "refusé"
  REMBOURSE: "remboursé"
};

const METHODES_PAIEMENT = {
  ESPECES: "espèces",
  CARTE_BANCAIRE: "carte_bancaire",
  CHEQUE: "chèque",  // Changed from "cheque" to "chèque"
  VIREMENT: "virement",
  AUTRE: "autre"
};

const RapportTab = ({
  intervention,
  setSelectedIntervention
}) => {
  const { updateIntervention } = usePlanningData();
  const { userData } = useContext(AuthContext);
  const [selectedImage, setSelectedImage] = useState(null); // État pour le Zoom
  // ÉTATS
  const [isSaving, setIsSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState(null);
  const [localDescription, setLocalDescription] = useState(intervention?.rapport_intervention?.description_travaux || '');
  const fileInputRef = useRef(null);

  // --- SAUVEGARDE MANUELLE DU RAPPORT (Texte + Statuts) ---
  const handleSaveRapport = async () => {
    setIsSaving(true);
    try {
      const updatedRapport = {
        ...intervention?.rapport_intervention,
        description_travaux: localDescription,
      };

      // 1. Appel API
      await updateIntervention(intervention?._id, {
        rapport_intervention: updatedRapport,
      });

      // 2. Mise à jour de l'état global (Parent)
      setSelectedIntervention((prev) => ({
        ...prev,
        rapport_intervention: updatedRapport,
      }));

      showNotification('Succès', 'Description enregistrée', 'success');
    } catch (err) {
      console.error(err);
      showNotification('Erreur', 'Échec de la sauvegarde', 'danger');
    } finally {
      setIsSaving(false);
    }
  };


  // --- FILE MANAGEMENT (Sauvegarde automatique après upload) ---
  const uploadAttachments = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('attachments', file));

      const uploadResponse = await postAPI('attachment', formData, `/rapport/${intervention?._id}`);

      if (uploadResponse?.files?.length) {
        const formattedFiles = uploadResponse.files.map((file) => ({
          originalName: file.originalName || file.name,
          fileName: file.fileName,
          mimeType: file.mimeType || file.type,
          size: file.size || 0,
          path: file.path,
          uploadedAt: new Date().toISOString(),
          uploadedBy: userData?._id || userData?.id,
        }));

        const updatedAttachments = [
          ...(intervention?.rapport_intervention?.attachments || []),
          ...formattedFiles,
        ];

        // Pour les fichiers, on garde l'auto-save pour éviter de perdre l'upload
        await updateIntervention(intervention?._id, {
          rapport_intervention: {
            ...intervention?.rapport_intervention,
            attachments: updatedAttachments,
          },
        });

        setSelectedIntervention((prev) => ({
          ...prev,
          rapport_intervention: { ...prev.rapport_intervention, attachments: updatedAttachments },
        }));
        showNotification('Succès', 'Fichiers ajoutés', 'success');
      }
    } catch (err) {
      showNotification('Erreur', "Échec de l'upload", 'danger');
    } finally {
      setUploading(false);
    }
  };

  const deleteAttachment = async (attachmentPath) => {
    if (!window.confirm("Supprimer ce fichier ?")) return;
    setDeletingAttachment(attachmentPath);
    try {
      await deleteAPI('attachment', `/${attachmentPath}`);
      const filtered = intervention?.rapport_intervention?.attachments?.filter(att => att.path !== attachmentPath);

      await updateIntervention(intervention?._id, {
        rapport_intervention: { ...intervention?.rapport_intervention, attachments: filtered },
      });

      setSelectedIntervention((prev) => ({
        ...prev,
        rapport_intervention: { ...prev.rapport_intervention, attachments: filtered },
      }));
    } finally {
      setDeletingAttachment(null);
    }
  };

  const getFileType = (file) => {
    const mime = file?.mimeType || '';
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime === 'application/pdf') return 'pdf';
    return 'file';
  };


  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = '/coreService/' + file.path;
    link.download = file.originalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <div className="p-3 bg-light min-vh-100">
      {/* SECTION DESCRIPTION */}

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <CardBody className="p-4">
          <h6 className="fw-bold mb-3 text-dark">Compte Rendu des Travaux</h6>
          <textarea
            rows={10}
            className="form-control border-light bg-white shadow-none mb-3"
            style={{ borderRadius: '12px', resize: 'none' }}
            // On utilise l'état LOCAL ici
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Décrivez ici les détails techniques..."
          />
          <div className="d-flex justify-content-end align-items-center gap-3">
            {/* Indicateur de modification non sauvegardée */}
            {localDescription !== (intervention?.rapport_intervention?.description_travaux || '') && (
              <small className="text-danger">
                Modifications non enregistrées
              </small>
            )}

            <Button
              variant="primary"
              className="px-4 rounded-pill fw-bold shadow-sm"
              onClick={handleSaveRapport}
              disabled={isSaving || intervention?.statut == "termine"}
            >
              {isSaving ? <Spinner size="sm" className="me-2" /> : <i className="fas fa-save me-2"></i>}
              Sauvegarder
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* SECTION DOCUMENTS */}
      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <CardBody className="p-4">
          <h6 className="fw-bold mb-3 text-dark">Documents & Médias</h6>
          <div
            className="p-4 border border-2 border-dashed rounded-4 text-center bg-white cursor-pointer hover-bg-light"
            onClick={() => fileInputRef.current?.click()}
            style={{ borderColor: '#dee2e6' }}
          >
            <input type="file" ref={fileInputRef} className="d-none" multiple onChange={(e) => uploadAttachments(Array.from(e.target.files))} />
            {uploading ? <Spinner variant="primary" /> : <i className="fas fa-cloud-upload-alt fa-2x text-muted mb-2"></i>}
            <p className="mb-0 fw-bold small">{uploading ? 'Envoi en cours...' : 'Cliquer pour ajouter des photos/vidéos'}</p>
          </div>

          <div className="mt-4">
            <Row className="g-3">
              {intervention?.rapport_intervention?.attachments?.map((file, idx) => {
                const type = getFileType(file);
                const isImg = file?.mimeType?.startsWith('image/');
                const fileUrl = '/coreService/' + file?.path;
                return (
                  <Col xs={6} md={4} key={idx}>
                    <div className="position-relative border rounded-3 overflow-hidden bg-white shadow-sm h-100">
                      <Button
                        variant="danger" size="sm" className="position-absolute top-0 end-0 m-1 rounded-circle"
                        style={{ zIndex: 10, width: '22px', height: '22px', padding: 0, fontSize: '10px' }}
                        onClick={() => deleteAttachment(file.path)}
                      >
                        <Icon icon='Close'></Icon>
                      </Button>
                      <div
                        className="ratio ratio-4x3 bg-light cursor-pointer"
                        onClick={() => setSelectedImage(file)}
                      >
                        {isImg ? (
                          <img src={fileUrl} className="object-fit-cover" alt="preview" />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center text-muted"> Cliquez pour voir
                          </div>
                        )}
                      </div>
                      <div className="p-2 border-top bg-white">
                        <small className="text-truncate d-block fw-bold" title={file.originalName}>{file.originalName}</small>
                        <small className="text-muted" style={{ fontSize: '10px' }}>{moment(file.uploadedAt).format('DD/MM HH:mm')}</small>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        </CardBody>
      </Card>
      {(intervention?.type === 'pose' || intervention?.type === 'devis') && (
        <Card className="border-0 shadow-sm rounded-4">
          <CardBody className="p-4">
            <h6 className="fw-bold mb-3 text-dark">Statut de fin d'intervention</h6>

            {intervention?.type === 'pose' ? (
              <div className="form-check form-switch p-0 d-flex align-items-center  border p-3 rounded-3 bg-white shadow-sm gap-2">
                <input
                  className="form-check-input ms-0"
                  type="checkbox"
                  role="switch"
                  id="poseSwitch"
                  style={{ width: '45px', height: '22px' }}
                  checked={!!intervention?.rapport_intervention?.pose_faite}
                  onChange={async (e) => {
                    const value = e.target.checked;
                    await updateIntervention(intervention?._id, {
                      rapport_intervention: {
                        ...intervention?.rapport_intervention,
                        pose_faite: value
                      }
                    });
                    setSelectedIntervention(prev => ({
                      ...prev,
                      rapport_intervention: { ...prev.rapport_intervention, pose_faite: value }
                    }));
                  }}
                />
                <label className="form-check-label fw-bold cursor-pointer" htmlFor="poseSwitch">
                  Pose terminée
                </label>

              </div>
            ) : (
              <div className="form-check form-switch p-0 d-flex align-items-center  border p-3 rounded-3 bg-white shadow-sm gap-2">
                <input
                  className="form-check-input ms-0"
                  type="checkbox"
                  role="switch"
                  id="devisSwitch"
                  style={{ width: '45px', height: '22px' }}
                  checked={!!intervention?.rapport_intervention?.pose_sur_place}
                  onChange={async (e) => {
                    const value = e.target.checked;
                    await updateIntervention(intervention?._id, {
                      rapport_intervention: {
                        ...intervention?.rapport_intervention,
                        pose_sur_place: value
                      }
                    });
                    setSelectedIntervention(prev => ({
                      ...prev,
                      rapport_intervention: { ...prev.rapport_intervention, pose_sur_place: value }
                    }));
                  }}
                />
                <label className="form-check-label fw-bold cursor-pointer" htmlFor="devisSwitch">
                  Devis accepté & Pose faite sur place
                </label>

              </div>
            )}
          </CardBody>
        </Card>
      )}

       {(intervention?.type === 'deblocage' || intervention?.type === 'devis') && (
        <Card className="border-0 shadow-sm rounded-4">
          <CardBody className="p-4">
<div className="form-check form-switch p-0 d-flex align-items-center  border p-3 rounded-3 bg-white shadow-sm gap-2">
                <input
                  className="form-check-input ms-0"
                  type="checkbox"
                  role="switch"
                  id="poseSwitch"
                  style={{ width: '45px', height: '22px' }}
                  checked={!!intervention?.pas_devis}
                  onChange={async (e) => {
                    const value = e.target.checked;
                    await updateIntervention(intervention?._id, {
                     pas_devis: value
                    });
                    setSelectedIntervention(prev => ({
                      ...prev,
                      pas_devis: value
                    }));
                  }}
                />
                <label className="form-check-label fw-bold cursor-pointer" htmlFor="poseSwitch">
                  Pas de devis
                </label>

              </div>
          </CardBody>
        </Card>
      )}
      {!!selectedImage && (
        <ImageVisualisation selectedImage={selectedImage} setSelectedImage={setSelectedImage} />
      )}
    </div>
  );
};


export default RapportTab;